import { ValidationError } from '@ibe/errors';
/**
 * Date handling.
 *
 * Two rules run through this module. First, everything is UTC — the master
 * prompt fixes it, and a booking engine spanning timezones cannot afford
 * ambiguity about what "2026-08-06" means. Second, stay dates are calendar
 * dates, not instants: a guest checking in on the 6th checks in on the 6th
 * regardless of the server's clock, so they are handled as `YYYY-MM-DD`
 * strings and only converted to `Date` at UTC midnight when arithmetic needs it.
 */
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/u;
const MS_PER_DAY = 86_400_000;
const MONTH_OFFSET = 1;
const ISO_DATE_LENGTH = 10;
function invalidDate(value, field) {
    throw new ValidationError([{ field, message: `Not a valid YYYY-MM-DD date: ${value}`, code: 'invalid_date' }], { message: 'Invalid date' });
}
/** Whether a string is a real calendar date in `YYYY-MM-DD` form. */
export function isIsoDate(value) {
    const match = ISO_DATE_PATTERN.exec(value);
    if (match === null)
        return false;
    const [, year = '', month = '', day = ''] = match;
    const date = new Date(Date.UTC(Number(year), Number(month) - MONTH_OFFSET, Number(day)));
    // Round-tripping catches impossible dates that Date silently rolls over,
    // such as 2026-02-30 becoming 2026-03-02.
    return toIsoDate(date) === value;
}
/** Converts a Date to its UTC calendar date. */
export function toIsoDate(date) {
    return date.toISOString().slice(0, ISO_DATE_LENGTH);
}
/** Parses a calendar date to a Date at UTC midnight. */
export function parseIsoDate(value, field = 'date') {
    if (!isIsoDate(value))
        invalidDate(value, field);
    return new Date(`${value}T00:00:00.000Z`);
}
/** Today's calendar date in UTC. */
export function todayUtc(now = new Date()) {
    return toIsoDate(now);
}
/** Adds days (negative subtracts), staying on the calendar. */
export function addDays(value, days) {
    const date = parseIsoDate(value);
    return toIsoDate(new Date(date.getTime() + days * MS_PER_DAY));
}
/**
 * Nights between arrival and departure.
 *
 * The booking industry's unit: 6th to 8th is two nights, not three days. All
 * pricing and availability iteration is driven from this.
 */
export function nightsBetween(arrival, departure) {
    const from = parseIsoDate(arrival, 'arrival');
    const to = parseIsoDate(departure, 'departure');
    return Math.round((to.getTime() - from.getTime()) / MS_PER_DAY);
}
/**
 * Every night in a stay: arrival inclusive, departure exclusive.
 *
 * Departure is excluded because no room is occupied on the checkout night —
 * getting this boundary wrong is the classic off-by-one that overcharges by a
 * night and double-books the turnover day.
 */
export function nightsInStay(arrival, departure) {
    const nights = nightsBetween(arrival, departure);
    if (nights <= 0)
        return [];
    return Array.from({ length: nights }, (_unused, index) => addDays(arrival, index));
}
/** Whether two half-open date ranges overlap. */
export function rangesOverlap(firstStart, firstEnd, secondStart, secondEnd) {
    return firstStart < secondEnd && secondStart < firstEnd;
}
const SATURDAY = 6;
const SUNDAY = 0;
/** Whether a calendar date falls on a Saturday or Sunday in UTC. */
export function isWeekend(value) {
    const day = parseIsoDate(value).getUTCDay();
    return day === SATURDAY || day === SUNDAY;
}
/** Validates a stay range, returning the night count. */
export function assertValidStay(arrival, departure) {
    const nights = nightsBetween(arrival, departure);
    if (nights <= 0) {
        throw new ValidationError([
            {
                field: 'departure',
                message: 'Departure must be after arrival',
                code: 'invalid_date_range',
            },
        ], { message: 'Invalid stay dates' });
    }
    return nights;
}
/** Current instant as an ISO-8601 timestamp. The platform's log/audit format. */
export function nowIso(now = new Date()) {
    return now.toISOString();
}
//# sourceMappingURL=date.js.map